import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Clock,
  AlertTriangle,
  AlertCircle,
  Layers,
  Server,
  Skull
} from 'lucide-react';
import { SystemHealth as SystemHealthType } from '@utils/types/back-office/operations';

/* eslint-disable no-restricted-syntax -- Semantic system-health thresholds (green/yellow/red) */

interface SystemHealthProps {
  data: SystemHealthType | null;
  isLoading: boolean;
}

export default function SystemHealth({ data, isLoading }: SystemHealthProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const formatProcessingSpeed = (minutes: number) => {
    if (minutes < 1) {
      const seconds = Math.round(minutes * 60);
      return `${seconds}s`;
    }
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    if (secs > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${mins}m`;
  };

  // Turnaround as a ratio of audio length: 0.33 reads as "1/3", i.e. a 10-minute
  // file finished in ~3m20s. Shown as a fraction under 1 (the intuitive form for
  // "faster than realtime") and as a multiplier at or above 1.
  const formatRatio = (ratio: number) => {
    if (!ratio || ratio <= 0) return '-';
    if (ratio >= 1) return `${ratio.toFixed(1)}x`;
    return `1/${Math.round(1 / ratio)}`;
  };

  const getErrorCountColor = (count: number) => {
    if (count < 5) return '#22C55E'; // green
    if (count < 20) return '#EAB308'; // yellow
    return '#EF4444'; // red
  };

  /* eslint-disable no-restricted-syntax -- Semantic colors for metrics */
  const metrics = [
    {
      id: 'queue-depth',
      label: t('queue-depth'),
      value: data?.queue_depth ?? 0,
      icon: Layers,
      description: `${data?.queue_processing ?? 0} ${t('processing')}, ${data?.queue_queued ?? 0} ${t('queued')}`,
      // Only meaningful when some queued work is parked behind the priority
      // threshold: shows how much of the backlog is actionable now vs waiting
      // on throttled orgs, so a queue driven by throttling reads differently
      // from a processing stall.
      subNote:
        data?.queue_queued_deprioritized && data.queue_queued_deprioritized > 0
          ? `${data.queue_queued_actionable ?? 0} ${t('queue-active')} · ${data.queue_queued_deprioritized} ${t('queue-parked')} (>${data.queue_priority_threshold ?? 0})`
          : undefined,
      subNoteHint: t('queue-parked-hint'),
      color:
        data?.queue_depth && data.queue_depth > 100 ? '#EAB308' : undefined,
      iconBg: 'rgba(59, 130, 246, 0.1)',
      iconColor: '#3B82F6'
    },
    {
      id: 'processing-speed',
      label: t('processing-speed'),
      value: data?.processing_ratio
        ? formatRatio(data.processing_ratio)
        : formatProcessingSpeed(data?.processing_speed_minutes ?? 0),
      icon: Clock,
      // Median, not mean: the queue is priority-ordered, so a handful of very
      // long files or sessions parked behind a throttled org used to drag the
      // headline number away from anything a real user experienced. p90 is
      // shown next to it so the tail is not hidden by the median.
      description: `${t('median-processing-time')} ${formatProcessingSpeed(data?.processing_speed_minutes ?? 0)}${
        data?.processing_p90_minutes
          ? ` · p90 ${formatProcessingSpeed(data.processing_p90_minutes)}`
          : ''
      }`,
      subNote: data?.processing_sample_size
        ? `${data.processing_sample_size} ${t('sessions-last-24h')}`
        : undefined,
      isText: true,
      iconBg: 'rgba(16, 185, 129, 0.1)',
      iconColor: '#10B981'
    },
    {
      id: 'error-count',
      label: t('error-count'),
      value: data?.error_count_24h ?? 0,
      icon: AlertTriangle,
      description: `${t('last-24-hours')} (${data?.queue_failed ?? 0} ${t('failed')})`,
      color: getErrorCountColor(data?.error_count_24h ?? 0),
      iconBg: 'rgba(245, 158, 11, 0.1)',
      iconColor: '#F59E0B'
    },
    {
      id: 'stranded-sessions',
      label: t('stranded-sessions'),
      value: data?.stranded_count ?? 0,
      icon: AlertCircle,
      description: t('needs-attention'),
      color:
        data?.stranded_count && data.stranded_count > 0 ? '#EF4444' : undefined,
      iconBg: 'rgba(239, 68, 68, 0.1)',
      iconColor: '#EF4444'
    },
    {
      id: 'stuck-processing',
      label: t('stuck-processing'),
      value: data?.stuck_processing_count ?? 0,
      icon: Server,
      description: t('requires-intervention'),
      color:
        data?.stuck_processing_count && data.stuck_processing_count > 0
          ? '#F97316'
          : undefined,
      iconBg: 'rgba(249, 115, 22, 0.1)',
      iconColor: '#F97316'
    },
    {
      id: 'dead-letter',
      label: t('dead-letter-queue'),
      value: data?.queue_dead_letter ?? 0,
      icon: Skull,
      description: t('unrecoverable-items'),
      color:
        data?.queue_dead_letter && data.queue_dead_letter > 0
          ? '#EF4444'
          : undefined,
      iconBg: 'rgba(107, 114, 128, 0.1)',
      iconColor: '#6B7280'
    }
  ];
  /* eslint-enable no-restricted-syntax */

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div
            key={i}
            className="rounded-2xl p-3 animate-pulse"
            style={{
              backgroundColor: getColour('secondLayer'),
              border: `1px solid ${getColour('border')}`
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className="h-6 w-6 rounded-2xl"
                style={{ backgroundColor: getColour('thirdLayer') }}
              />
              <div
                className="h-3 flex-1 rounded-2xl"
                style={{ backgroundColor: getColour('thirdLayer') }}
              />
            </div>
            <div
              className="h-5 w-12 rounded-2xl"
              style={{ backgroundColor: getColour('thirdLayer') }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      {metrics.map(metric => {
        const Icon = metric.icon;
        const hasWarning = metric.color !== undefined;

        return (
          <div
            key={metric.id}
            className="rounded-2xl p-3"
            style={{
              backgroundColor: getColour('secondLayer'),
              border: `1px solid ${hasWarning ? metric.color : getColour('border')}`
            }}
          >
            {/* eslint-disable no-restricted-syntax -- Semantic colors for metrics */}
            <div className="flex items-center gap-2 mb-1">
              <div
                className="p-1 rounded-2xl flex-shrink-0"
                style={{ backgroundColor: metric.iconBg }}
              >
                <Icon
                  className="h-3.5 w-3.5"
                  style={{ color: metric.color || metric.iconColor }}
                />
              </div>
              <p
                className="text-xs font-medium truncate"
                style={{ color: getColour('inactiveText') }}
                title={metric.label}
              >
                {metric.label}
              </p>
            </div>
            <div className="flex items-center justify-between gap-1">
              <p
                className="text-lg font-bold"
                style={{ color: metric.color || getColour('normalText') }}
              >
                {metric.value}
              </p>
              {hasWarning && (
                <span
                  className="text-[10px] px-1 py-0.5 rounded-2xl"
                  style={{
                    backgroundColor:
                      metric.color === '#EF4444'
                        ? 'rgba(239, 68, 68, 0.1)'
                        : metric.color === '#EAB308'
                          ? 'rgba(234, 179, 8, 0.1)'
                          : metric.color === '#F97316'
                            ? 'rgba(249, 115, 22, 0.1)'
                            : 'rgba(34, 197, 94, 0.1)',
                    color: metric.color
                  }}
                >
                  {metric.color === '#EF4444'
                    ? t('critical')
                    : metric.color === '#EAB308'
                      ? t('warning')
                      : metric.color === '#F97316'
                        ? t('attention')
                        : t('ok')}
                </span>
              )}
            </div>
            <p
              className="text-[10px] truncate mt-0.5"
              style={{ color: getColour('inactiveText') }}
              title={metric.description}
            >
              {metric.description}
            </p>
            {metric.subNote && (
              <p
                className="text-[10px] font-medium truncate mt-0.5"
                style={{ color: '#EAB308' }}
                title={metric.subNoteHint || metric.subNote}
              >
                {metric.subNote}
              </p>
            )}
            {/* eslint-enable no-restricted-syntax */}
          </div>
        );
      })}
    </div>
  );
}
